import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import axiosInstance from '../AxiosInstance';

type AdminUserDto = {
    id: string;
    email: string | null;
    userName: string | null;
    organisations: string[];
    emailConfirmed: boolean;
    lockoutEnabled: boolean;
    lockoutEnd: string | null;
    accessFailedCount: number;
    isBlocked: boolean;
};

const formatDateTime = (value: string | null): string => {
    if (!value) {
        return 'Never';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
};

const UserBlockManagement = () => {
    const [users, setUsers] = useState<AdminUserDto[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.get<AdminUserDto[]>('/auth/users');
            setUsers(response.data);
        } catch (fetchError: unknown) {
            const message =
                (fetchError as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message ||
                (fetchError as { message?: string }).message ||
                'Failed to fetch users.';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
        setSelectedUserIds(selectionModel.map((id) => String(id)));
    };

    const handleUpdateBlockStatus = async (block: boolean) => {
        if (selectedUserIds.length === 0) {
            return;
        }

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axiosInstance.post('/auth/users/block-status', {
                userIds: selectedUserIds,
                block
            });

            const updatedCount = response.data?.updatedCount ?? selectedUserIds.length;
            setSuccess(`${block ? 'Blocked' : 'Unblocked'} ${updatedCount} user(s) successfully.`);
            setSelectedUserIds([]);
            await fetchUsers();
        } catch (updateError: unknown) {
            const message =
                (updateError as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message ||
                (updateError as { message?: string }).message ||
                'Failed to update selected users.';
            setError(message);
        } finally {
            setActionLoading(false);
        }
    };

    const columns = useMemo<GridColDef<AdminUserDto>[]>(() => [
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 220
        },
        {
            field: 'userName',
            headerName: 'Username',
            flex: 1,
            minWidth: 180
        },
        {
            field: 'organisations',
            headerName: 'Organisation',
            flex: 1,
            minWidth: 180,
            valueGetter: (_value, row) => row.organisations.join(', ')
        },
        {
            field: 'isBlocked',
            headerName: 'Status',
            minWidth: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Blocked' : 'Active'}
                    size="small"
                    color={params.value ? 'error' : 'success'}
                    variant="outlined"
                />
            )
        },
        {
            field: 'lockoutEnd',
            headerName: 'Lockout End',
            minWidth: 190,
            valueFormatter: (value) => formatDateTime(value as string | null)
        },
        {
            field: 'emailConfirmed',
            headerName: 'Email Confirmed',
            minWidth: 150,
            type: 'boolean'
        },
        {
            field: 'lockoutEnabled',
            headerName: 'Lockout Enabled',
            minWidth: 150,
            type: 'boolean'
        },
        {
            field: 'accessFailedCount',
            headerName: 'Failed Logins',
            minWidth: 130,
            type: 'number'
        }
    ], []);

    return (
        <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                User Access Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select one or more users, then block or unblock them.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleUpdateBlockStatus(true)}
                    disabled={actionLoading || selectedUserIds.length === 0}
                >
                    Block Selected
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleUpdateBlockStatus(false)}
                    disabled={actionLoading || selectedUserIds.length === 0}
                >
                    Unblock Selected
                </Button>
                {actionLoading && <CircularProgress size={22} />}
            </Stack>

            <Box sx={{ height: 520, width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    loading={loading}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedUserIds}
                    onRowSelectionModelChange={handleSelectionChange}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                                page: 0
                            }
                        }
                    }}
                />
            </Box>
        </Paper>
    );
};

export default UserBlockManagement;
